'use client'

import Cards from '@/ui/assets/components/cards.inline.svg'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { Clock, FileCheck, FileSearch, HelpCircle, RefreshCw, ShieldX } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { KYCStatus } from '../types/CardState'
import {
  KYC_AUTO_POLL_INTERVAL_MS,
  RAIN_NEEDS_ACTION_STATUSES,
  RAIN_REJECTED_STATUSES,
  SUPPORT_URLS,
} from '../constants'

interface CardKYCPendingProps {
  kycStatus?: KYCStatus
  rainStatus?: string
  onRefresh: () => void
  isLoading?: boolean
  error?: string
}

export function CardKYCPending({ kycStatus, rainStatus, onRefresh, isLoading, error }: CardKYCPendingProps) {
  const isKycApproved = kycStatus === KYCStatus.APPROVED
  const isRainRejected = isKycApproved && rainStatus && RAIN_REJECTED_STATUSES.includes(rainStatus)
  const isRainNeedsAction = isKycApproved && rainStatus && RAIN_NEEDS_ACTION_STATUSES.includes(rainStatus)
  const isRainPending =
    isKycApproved &&
    rainStatus &&
    !RAIN_REJECTED_STATUSES.includes(rainStatus) &&
    !RAIN_NEEDS_ACTION_STATUSES.includes(rainStatus) &&
    rainStatus !== 'approved'

  const onRefreshRef = useRef(onRefresh)
  onRefreshRef.current = onRefresh
  const isLoadingRef = useRef(isLoading)
  isLoadingRef.current = isLoading

  useEffect(() => {
    const isTerminal = isRainRejected || isRainNeedsAction
    if (isTerminal) return

    const id = setInterval(() => {
      if (!isLoadingRef.current) {
        onRefreshRef.current()
      }
    }, KYC_AUTO_POLL_INTERVAL_MS)

    return () => clearInterval(id)
  }, [isRainRejected, isRainNeedsAction])

  if (isRainRejected) {
    return <RainRejectedView rainStatus={rainStatus} error={error} onRefresh={onRefresh} isLoading={isLoading} />
  }

  if (isRainNeedsAction) {
    return <RainNeedsActionView rainStatus={rainStatus} error={error} onRefresh={onRefresh} isLoading={isLoading} />
  }

  const getStatusMessage = () => {
    if (isRainPending) {
      return 'Your identity verification is complete! We are now finalizing your account. This usually takes a few minutes.'
    }

    switch (kycStatus) {
      case KYCStatus.PENDING:
        return 'Your KYC application is currently being processed. This usually takes 1-2 business days.'
      case KYCStatus.REVIEW:
        return 'Your KYC documents are under review. We may contact you if additional information is needed.'
      case KYCStatus.INITIATED:
        return 'Your KYC process has been initiated. Please complete any pending steps if required.'
      default:
        return 'Your KYC application is being processed. Please check back later.'
    }
  }

  const getStatusTitle = () => {
    if (isRainPending) {
      return 'Finalizing Your Account'
    }

    switch (kycStatus) {
      case KYCStatus.PENDING:
        return 'KYC Application Pending'
      case KYCStatus.REVIEW:
        return 'Documents Under Review'
      case KYCStatus.INITIATED:
        return 'KYC Process Initiated'
      default:
        return 'KYC Application Pending'
    }
  }

  const getStatusIcon = () => {
    switch (kycStatus) {
      case KYCStatus.REVIEW:
        return <FileSearch className="h-6 w-6 text-primary" />
      case KYCStatus.INITIATED:
        return <FileCheck className="h-6 w-6 text-primary" />
      default:
        return <Clock className="h-6 w-6 text-primary" />
    }
  }

  return (
    <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-start">
      <div className="flex flex-1 flex-col gap-6">
        <Panel.Wrapper className="relative w-full max-w-xl overflow-hidden">
          <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-primary/10 via-transparent to-gradient-green/10" />

          <Panel.Content className="relative flex flex-col gap-6 p-8">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-primary/30 bg-primary/10">
                {getStatusIcon()}
              </div>
              <div>
                <Typography variant="h3" gradient>
                  {getStatusTitle()}
                </Typography>
              </div>
            </div>

            <Typography variant="p" className="text-muted-foreground">
              {getStatusMessage()}
            </Typography>

            <div className="flex flex-col items-center gap-4 rounded-lg border border-border/50 bg-background/30 p-6">
              <div className="relative">
                <div className="h-16 w-16 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-primary" />
                </div>
              </div>

              <Typography variant="p" className="text-center text-muted-foreground text-sm">
                {isLoading ? 'Auto-checking your status...' : 'Status is checked automatically every 30 seconds'}
              </Typography>
            </div>

            {error && (
              <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
                <Typography variant="p" className="text-red-400 text-sm">
                  {error}
                </Typography>
              </div>
            )}

            <Button
              onClick={onRefresh}
              variant="outline"
              size="lg"
              className="w-full transition-all duration-300 hover:border-primary/50 hover:bg-primary/5"
              disabled={isLoading}
              prefixIcon={
                isLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />
              }
            >
              {isLoading ? 'Checking Status...' : 'Check Status'}
            </Button>
          </Panel.Content>
        </Panel.Wrapper>
      </div>

      <div className="relative flex items-center justify-center lg:flex-1">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-[250px] w-[350px] rounded-full bg-gradient-to-br from-primary/15 to-gradient-green/15 blur-[60px]" />
        </div>
        <div className="relative opacity-70 transition-opacity duration-500 hover:opacity-90">
          <Cards className="h-auto w-full max-w-sm" />
        </div>
      </div>
    </div>
  )
}

function RainRejectedView({
  rainStatus,
  error,
  onRefresh,
  isLoading,
}: {
  rainStatus: string
  error?: string
  onRefresh: () => void
  isLoading?: boolean
}) {
  const getTitle = () => {
    switch (rainStatus) {
      case 'denied':
        return 'Card Application Denied'
      case 'locked':
        return 'Card Account Locked'
      case 'canceled':
        return 'Card Application Canceled'
      default:
        return 'Card Application Rejected'
    }
  }

  const getMessage = () => {
    switch (rainStatus) {
      case 'denied':
        return 'Your card application has been denied. This could be due to compliance requirements or incomplete verification.'
      case 'locked':
        return 'Your card account has been locked. This may be a temporary measure due to security or compliance checks.'
      case 'canceled':
        return 'Your card application has been canceled. You may reapply if you believe this was in error.'
      default:
        return 'Your card application could not be processed. Please contact support for more information.'
    }
  }

  return (
    <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-start">
      <div className="flex flex-1 flex-col gap-6">
        <Panel.Wrapper className="relative w-full max-w-xl overflow-hidden">
          <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-red-500/10 via-transparent to-red-400/5" />

          <Panel.Content className="relative flex flex-col gap-6 p-8">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-red-500/30 bg-red-500/10">
                <ShieldX className="h-6 w-6 text-red-400" />
              </div>
              <div>
                <Typography variant="h3" className="text-red-400">
                  {getTitle()}
                </Typography>
              </div>
            </div>

            <Typography variant="p" className="text-muted-foreground">
              {getMessage()}
            </Typography>

            <div className="flex flex-col gap-3 rounded-lg border border-border/50 bg-background/30 p-4">
              <div className="flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                <Typography variant="p" className="text-muted-foreground text-sm">
                  Incomplete or unclear verification documents
                </Typography>
              </div>
              <div className="flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                <Typography variant="p" className="text-muted-foreground text-sm">
                  Regulatory compliance requirements
                </Typography>
              </div>
              <div className="flex items-center gap-2">
                <HelpCircle className="h-4 w-4 text-muted-foreground" />
                <Typography variant="p" className="text-muted-foreground text-sm">
                  Information mismatch between documents
                </Typography>
              </div>
            </div>

            <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
              <Typography variant="p" className="text-red-400 text-sm">
                <strong>What you can do:</strong> Contact our support team for detailed feedback on your application
                {rainStatus === 'canceled' ? ' or try submitting a new application.' : '.'}
              </Typography>
            </div>

            {error && (
              <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
                <Typography variant="p" className="text-red-400 text-sm">
                  {error}
                </Typography>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => window.open(SUPPORT_URLS.KYC_SUPPORT, '_blank')}
                size="lg"
                className="w-full transition-all duration-300 hover:opacity-90 hover:shadow-lg"
              >
                Contact Support
              </Button>
              <Button
                onClick={onRefresh}
                variant="outline"
                size="lg"
                className="w-full transition-all duration-300 hover:border-primary/50 hover:bg-primary/5"
                disabled={isLoading}
                prefixIcon={
                  isLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />
                }
              >
                {isLoading ? 'Checking Status...' : 'Refresh Status'}
              </Button>
            </div>
          </Panel.Content>
        </Panel.Wrapper>
      </div>

      <div className="relative flex items-center justify-center lg:flex-1">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-[250px] w-[350px] rounded-full bg-gradient-to-br from-red-500/10 to-red-400/10 blur-[60px]" />
        </div>
        <div className="relative opacity-60 grayscale transition-all duration-500 hover:opacity-80 hover:grayscale-0">
          <Cards className="h-auto w-full max-w-sm" />
        </div>
      </div>
    </div>
  )
}

function RainNeedsActionView({
  rainStatus,
  error,
  onRefresh,
  isLoading,
}: {
  rainStatus: string
  error?: string
  onRefresh: () => void
  isLoading?: boolean
}) {
  const isNeedsInfo = rainStatus === 'needsInformation'

  return (
    <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-start">
      <div className="flex flex-1 flex-col gap-6">
        <Panel.Wrapper className="relative w-full max-w-xl overflow-hidden">
          <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-amber-500/10 via-transparent to-amber-400/5" />

          <Panel.Content className="relative flex flex-col gap-6 p-8">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full border-2 border-amber-500/30 bg-amber-500/10">
                <FileSearch className="h-6 w-6 text-amber-400" />
              </div>
              <div>
                <Typography variant="h3" className="text-amber-400">
                  {isNeedsInfo ? 'Additional Information Required' : 'Verification Required'}
                </Typography>
              </div>
            </div>

            <Typography variant="p" className="text-muted-foreground">
              {isNeedsInfo
                ? 'Your KYC is approved, but we need additional information to complete your card setup. Please check your email or contact support for details.'
                : 'Your KYC is approved, but additional identity verification is needed to finalize your card account. Please check your email for verification steps.'}
            </Typography>

            <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 p-4">
              <Typography variant="p" className="text-amber-400 text-sm">
                <strong>Action needed:</strong>{' '}
                {isNeedsInfo
                  ? 'Check your email for instructions on what information to provide.'
                  : 'Complete the additional verification steps sent to your email.'}
              </Typography>
            </div>

            {error && (
              <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-4">
                <Typography variant="p" className="text-red-400 text-sm">
                  {error}
                </Typography>
              </div>
            )}

            <div className="flex flex-col gap-3">
              <Button
                onClick={() => window.open(SUPPORT_URLS.KYC_SUPPORT, '_blank')}
                size="lg"
                className="w-full transition-all duration-300 hover:opacity-90 hover:shadow-lg"
              >
                Contact Support
              </Button>
              <Button
                onClick={onRefresh}
                variant="outline"
                size="lg"
                className="w-full transition-all duration-300 hover:border-primary/50 hover:bg-primary/5"
                disabled={isLoading}
                prefixIcon={
                  isLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />
                }
              >
                {isLoading ? 'Checking Status...' : 'Refresh Status'}
              </Button>
            </div>
          </Panel.Content>
        </Panel.Wrapper>
      </div>

      <div className="relative flex items-center justify-center lg:flex-1">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-[250px] w-[350px] rounded-full bg-gradient-to-br from-amber-500/10 to-amber-400/10 blur-[60px]" />
        </div>
        <div className="relative opacity-70 transition-opacity duration-500 hover:opacity-90">
          <Cards className="h-auto w-full max-w-sm" />
        </div>
      </div>
    </div>
  )
}
